import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Rating,
  Divider,
} from "@mui/material";
import apiService from "../../../../service/Api";

interface Props {
  bookingId: string;
}

interface ReviewData {
  id: number;
  booking: number;
  contact: number | null;
  client_review: number;
  comment: string;
  improvement_suggestions: string[];
  would_recommend: string;
  recommendation_reason: string;
  created_at: string;
  updated_at: string;
}

interface ReviewResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ReviewData[];
}

const BookingReviewSummaryCard: React.FC<Props> = ({ bookingId }) => {
  const [review, setReview] = useState<ReviewData | null>(null);
  const [loading, setLoading] = useState(true);

 
  useEffect(() => {
    if (!bookingId) return;

    const fetchReview = async () => {
      try {
        setLoading(true);
        const response = await apiService.get<ReviewResponse>(
          `/api/bookings/booking-review/?booking=${bookingId}`,
        );
        setReview(response.data.results?.[0] || null);
      } catch (error) {
        console.error("Failed to fetch review", error);
        setReview(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReview();
  }, [bookingId]);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!review) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6">Client Review</Typography>

          <Typography mt={2} color="text.secondary">
            No review submitted yet
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ boxShadow: 0 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Client Review
        </Typography>

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            Rating
          </Typography>

          <Rating value={review.client_review} readOnly />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box mb={2}>
          <Typography fontWeight="bold">Comment</Typography>

          <Typography variant="body2">
            {review.comment || "No comment provided"}
          </Typography>
        </Box>

        <Box mb={2}>
          <Typography fontWeight="bold">Would Recommend</Typography>

          <Typography variant="body2">
            {review.would_recommend || "-"}
          </Typography>
        </Box>

        <Box mb={2}>
          <Typography fontWeight="bold">Recommendation Reason</Typography>

          <Typography variant="body2">
            {review.recommendation_reason || "-"}
          </Typography>
        </Box>

        <Box>
          <Typography fontWeight="bold">Improvement Suggestions</Typography>

          {review.improvement_suggestions?.length ? (
            review.improvement_suggestions.map((item, index) => (
              <Typography key={index} variant="body2">
                • {item}
              </Typography>
            ))
          ) : (
            <Typography variant="body2">No suggestions provided</Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default BookingReviewSummaryCard;
